import { FunctionComponent, useEffect, useState } from 'react';
import { useApiCall } from '@/hooks/useApiCall';
import { transactionService } from '../api/transactions/transaction.service';
import { TransactionModel } from '../types/TransactionModel';
import { PagePaginationResultDto, PageSortOrder } from '@/lib/api/pagination.page.dto';
import { SortChangedEvent, GridReadyEvent } from 'ag-grid-community';
import { TabbedPageLayoutBody } from '@/modules/application/components/TabbedPageLayoutBody';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import { GuestTransactionsGrid } from '../components/GuestTransactionsGrid';
import { useLayoutActions } from '@/providers/layout-actions-provider/LayoutActionsProvider';
import { TabbedPageLayout } from '@/modules/application/layouts/TabbedPageLayout';
import RequirePermissionComponent from '@/components/permissions/RequirePermissionComponent';
import { Permission } from '@/modules/users/submodules/roles/api/permissions.contracts';
import { GuestCreateTransactionModal } from '../components/GuestCreateTransactionModal';
import { GuestEditTransactionModal } from '../components/GuestEditTransactionModal';
import { TransactionTypeSelector } from '../components/TransactionTypeSelector';
import { TransactionSortOption, TransactionType } from '../api/transactions/transaction.contracts';
import { useSearchParams, useNavigate, useParams } from 'react-router';
import useQueryParamsFilters from '@/hooks/useQueryParamFilters';
import { CacheKey } from '@/providers/cache-provider/cache-key.enum';
import { FilterFieldName, FilterValues } from '@/components/filterbar/FilterBarContext';
import FilterBar from '@/components/filterbar/FilterBar';
import DateFilter from '@/components/filterbar/filters/DateFilter';
import GuestLocationFilter from '@/components/filterbar/filters/GuestLocationFilter';
import AssetTypeFilter from '@/components/filterbar/filters/AssetTypeFilter';
import TransactionTypeFilter from '@/components/filterbar/filters/TransactionTypeFilter';
import TransactionStatusFilter from '@/components/filterbar/filters/TransactionStatusFilter';
import TransactionSourceFilter from '@/components/filterbar/filters/TransactionSourceFilter';
import { useTranslation } from '@/lib';
import PaginationControls from '@/components/grid/PaginationControls';
import PagedResultDataText from '@/components/filterbar/PagedResultDataText';
import { ColumnID } from '@/components/grid/column-ids';
import { useExportToExcel } from '@/hooks/useExportToExcel';
import dayjs from 'dayjs';
import { useUser } from '@/modules/users/contexts/UserContext';
import { UserType } from '@/modules/users/api/users/users.contracts';

const TRANSACTION_FETCH_LIMIT = 1000;

export const GuestTransactionsPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { transactionId: transactionIdParam } = useParams<{ transactionId: string }>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTransactionType, setSelectedTransactionType] = useState<TransactionType>(TransactionType.Transfer);
  const { setActions } = useLayoutActions();
  const { exportToExcel } = useExportToExcel();

  const [searchParams] = useSearchParams();
  const { setFiltersToUrl, filters } = useQueryParamsFilters(
    {
      pageNumber: searchParams.get('pageNumber') ? Number.parseInt(searchParams.get('pageNumber')!) : 1,
      sortOption: TransactionSortOption.DATE,
      sortDirection: PageSortOrder.DESC,
    },
    CacheKey.TRANSACTION_FILTERS,
  );

  // Redirect non-guest users to regular transactions page
  useEffect(() => {
    if (user?.type !== UserType.GUEST) {
      navigate('/app/transactions');
    }
  }, [user, navigate]);

  const { data, isError, isLoading, setApiCallArg } = useApiCall<PagePaginationResultDto<TransactionModel>>(() => {
    const dateFrom = filters.current.dateFrom ? filters.current.dateFrom.toISOString() : undefined;
    const dateTo = filters.current.dateTo ? filters.current.dateTo.toISOString() : undefined;

    // For guest users, fetch transactions for their assigned locations only
    return transactionService.get(
      {
        dateFrom,
        dateTo,
        locationId: filters.current.locationId,
        assetTypeId: filters.current.assetTypeId,
        type: filters.current.transactionType,
        status: filters.current.transactionStatus,
        source: filters.current.transactionSource,
      },
      {
        page: filters.current.pageNumber,
        limit: TRANSACTION_FETCH_LIMIT,
        order: filters.current.sortDirection,
        sort: filters.current.sortOption,
      },
    );
  });

  useEffect(() => {
    setActions(
      <RequirePermissionComponent permission={Permission.TRANSACTIONS_EDIT}>
        <TransactionTypeSelector
          onTypeSelected={(type) => {
            setSelectedTransactionType(type);
            setIsCreateModalOpen(true);
          }}
        />
      </RequirePermissionComponent>,
    );

    return () => {
      setActions('');
    };
  }, [setActions]);

  const handleRowDoubleClicked = (transactionId: string) => {
    navigate(`/app/guest-transactions/overview/${transactionId}`);
  };

  const handleSearch = (filterValues: FilterValues, page?: number) => {
    setFiltersToUrl({
      ...filterValues,
      pageNumber: page ?? filters.current.pageNumber,
    });

    const dateFrom = filterValues.dateFrom ? filterValues.dateFrom.toISOString() : undefined;
    const dateTo = filterValues.dateTo ? filterValues.dateTo.toISOString() : undefined;

    setApiCallArg(() =>
      transactionService.get(
        {
          dateFrom,
          dateTo,
          locationId: filterValues.locationId,
          assetTypeId: filterValues.assetTypeId,
          type: filterValues.transactionType,
          status: filterValues.transactionStatus,
          source: filterValues.transactionSource,
        },
        {
          page: filterValues.pageNumber,
          limit: TRANSACTION_FETCH_LIMIT,
          order: filterValues.sortDirection,
          sort: filterValues.sortOption,
        },
      ),
    );
  };

  const onPageChanged = (event: React.ChangeEvent<unknown>, page: number) => {
    handleSearch(
      {
        ...filters.current,
        pageNumber: page,
      },
      page,
    );
  };

  const handleSortChanged = (event: SortChangedEvent<TransactionModel>) => {
    if (event.columns && event.columns.length > 0 && event.source === 'uiColumnSorted') {
      const changedColumn = event.columns.find((col) => col.getSort());
      if (!changedColumn) {
        handleSearch(
          {
            ...filters.current,
            sortOption: undefined,
            sortDirection: undefined,
          },
          1,
        );
        return;
      }

      const changedColumnId = changedColumn.getColId();

      let sortOption: TransactionSortOption | undefined;
      switch (changedColumnId) {
        case ColumnID.TRANSACTION_ID:
          sortOption = TransactionSortOption.TRANSACTION_ID;
          break;
        case ColumnID.TRANSACTION_TYPE:
          sortOption = TransactionSortOption.TYPE;
          break;
        case ColumnID.TRANSACTION_DATE:
          sortOption = TransactionSortOption.DATE;
          break;
        case ColumnID.TRANSACTION_FROM_LOCATION:
          sortOption = TransactionSortOption.FROM_LOCATION;
          break;
        case ColumnID.TRANSACTION_TO_LOCATION:
          sortOption = TransactionSortOption.TO_LOCATION;
          break;
        default:
          break;
      }

      let sortOrder: PageSortOrder | undefined;
      switch (changedColumn.getSort()) {
        case 'asc':
          sortOrder = PageSortOrder.ASC;
          break;
        case 'desc':
          sortOrder = PageSortOrder.DESC;
          break;
        default:
          sortOption = undefined;
          break;
      }

      handleSearch(
        {
          ...filters.current,
          sortOption,
          sortDirection: sortOrder,
        },
        1,
      );
    }
  };

  const handleGridReady = (event: GridReadyEvent<TransactionModel>) => {
    // Apply initial sort state
    if (filters.current.sortOption && filters.current.sortDirection) {
      let columnId: ColumnID | undefined;

      switch (filters.current.sortOption) {
        case TransactionSortOption.TRANSACTION_ID:
          columnId = ColumnID.TRANSACTION_ID;
          break;
        case TransactionSortOption.TYPE:
          columnId = ColumnID.TRANSACTION_TYPE;
          break;
        case TransactionSortOption.DATE:
          columnId = ColumnID.TRANSACTION_DATE;
          break;
        case TransactionSortOption.FROM_LOCATION:
          columnId = ColumnID.TRANSACTION_FROM_LOCATION;
          break;
        case TransactionSortOption.TO_LOCATION:
          columnId = ColumnID.TRANSACTION_TO_LOCATION;
          break;
        default:
          break;
      }

      if (columnId) {
        event.api.applyColumnState({
          state: [
            {
              colId: columnId,
              sort: filters.current.sortDirection === PageSortOrder.ASC ? 'asc' : 'desc',
            },
          ],
          defaultState: { sort: null },
        });
      }
    }
  };

  const handleTransactionCreated = () => {
    handleSearch(filters.current, filters.current.pageNumber);
  };

  const onExportToExcelClicked = async () => {
    const data: TransactionModel[] = [];

    const dateFrom = filters.current.dateFrom ? filters.current.dateFrom.toISOString() : undefined;
    const dateTo = filters.current.dateTo ? filters.current.dateTo.toISOString() : undefined;

    // Fetch first page
    const dataPage1 = await transactionService.get(
      {
        dateFrom,
        dateTo,
        locationId: filters.current.locationId,
        assetTypeId: filters.current.assetTypeId,
        type: filters.current.transactionType,
        status: filters.current.transactionStatus,
        source: filters.current.transactionSource,
      },
      {
        page: 1,
        limit: TRANSACTION_FETCH_LIMIT,
        order: filters.current.sortDirection ?? PageSortOrder.DESC,
        sort: filters.current.sortOption,
      },
    );

    if (!dataPage1.isSuccess || !dataPage1.payload) {
      return;
    }

    data.push(...dataPage1.payload.data);

    // Fetch additional pages if needed
    for (let i = 2; i <= (dataPage1.payload.totalPages || 1); i++) {
      const dataPage = await transactionService.get(
        {
          dateFrom,
          dateTo,
          locationId: filters.current.locationId,
          assetTypeId: filters.current.assetTypeId,
          type: filters.current.transactionType,
          status: filters.current.transactionStatus,
          source: filters.current.transactionSource,
        },
        {
          page: i,
          limit: TRANSACTION_FETCH_LIMIT,
          order: filters.current.sortDirection ?? PageSortOrder.DESC,
          sort: filters.current.sortOption,
        },
      );

      if (dataPage.isSuccess) {
        data.push(...dataPage.payload.data);
      }
    }

    if (data.length > 0) {
      exportToExcel(
        data.map((transaction) => ({
          transactionId: transaction.transactionId,
          type: transaction.type,
          status: transaction.status,
          source: transaction.source,
          date: dayjs(transaction.date).format('YYYY-MM-DD HH:mm:ss'),
          fromLocation: transaction.fromLocation?.name,
          toLocation: transaction.toLocation?.name,
          carrier: transaction.carrier?.name,
          documentNumber: transaction.documentNumber,
          referenceNumber: transaction.referenceNumber,
          notes: transaction.notes,
          createdAt: dayjs(transaction.createdAt).format('YYYY-MM-DD HH:mm:ss'),
          // Add asset details as additional columns
          ...transaction.details?.reduce<Record<string, number>>((acc, detail) => {
            acc[detail.assetType.name] = detail.amount;
            return acc;
          }, {}),
        })),
        `transactions_${dayjs().format('YYYY_MM_DD_HHmmss')}.xlsx`,
      );
    }
  };

  // Guest users see location filter with only their assigned locations
  const filterComponents = [
    {
      label: t('date_from'),
      filterName: FilterFieldName.dateFrom,
      filter: <DateFilter label={t('date_from')} filterField={FilterFieldName.dateFrom} />,
    },
    {
      label: t('date_to'),
      filterName: FilterFieldName.dateTo,
      filter: <DateFilter label={t('date_to')} filterField={FilterFieldName.dateTo} />,
    },
    {
      label: t('location'),
      filterName: FilterFieldName.locationId,
      filter: <GuestLocationFilter label={t('location')} />,
    },
    {
      label: t('asset_type'),
      filterName: FilterFieldName.assetTypeId,
      filter: <AssetTypeFilter label={t('asset_type')} />,
    },
    {
      label: t('type'),
      filterName: FilterFieldName.transactionType,
      filter: <TransactionTypeFilter label={t('type')} />,
    },
    {
      label: t('status'),
      filterName: FilterFieldName.transactionStatus,
      filter: <TransactionStatusFilter label={t('status')} />,
    },
    {
      label: t('source'),
      filterName: FilterFieldName.transactionSource,
      filter: <TransactionSourceFilter label={t('source')} />,
    },
  ];

  if (user?.type !== UserType.GUEST) {
    return null;
  }

  return (
    <TabbedPageLayout>
      <TabbedPageLayoutBody>
        <div className="flex h-full flex-col">
          <FilterBar
            className="mb-2"
            onSearch={handleSearch}
            initialFilterValues={filters.current}
            filterComponents={filterComponents}
            isLoading={isLoading}
            showAsPopover
          />
          {isError ? (
            <ErrorLoadingDataAlert />
          ) : (
            <div className="flex-1 overflow-hidden">
              <GuestTransactionsGrid
                data={data?.data}
                isLoading={isLoading}
                onRowDoubleClicked={handleRowDoubleClicked}
                onSortChanged={handleSortChanged}
                onGridReady={handleGridReady}
                onExportToExcelClicked={onExportToExcelClicked}
                onRefresh={() => {
                  // Trigger a refresh of the data
                  setApiCallArg(() =>
                    transactionService.get(
                      {
                        dateFrom: filters.current.dateFrom ? filters.current.dateFrom.toISOString() : undefined,
                        dateTo: filters.current.dateTo ? filters.current.dateTo.toISOString() : undefined,
                        locationId: filters.current.locationId,
                        assetTypeId: filters.current.assetTypeId,
                        type: filters.current.transactionType,
                        status: filters.current.transactionStatus,
                        source: filters.current.transactionSource,
                      },
                      {
                        page: filters.current.pageNumber,
                        limit: TRANSACTION_FETCH_LIMIT,
                        order: filters.current.sortDirection,
                        sort: filters.current.sortOption,
                      },
                    ),
                  );
                }}
              />
            </div>
          )}
          <div className="flex items-center gap-x-4">
            <PaginationControls
              isLoading={isLoading}
              totalPageCount={data?.totalPages ?? 1}
              currentPage={filters.current.pageNumber ?? 1}
              totalElements={data?.totalElements ?? 0}
              onChange={onPageChanged}
            />
            <PagedResultDataText data={data} name={t('transaction', { count: data?.totalElements ?? 0 })} />
          </div>
        </div>
      </TabbedPageLayoutBody>
      <GuestCreateTransactionModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleTransactionCreated}
        transactionType={selectedTransactionType}
      />
      {transactionIdParam && (
        <GuestEditTransactionModal
          open={true}
          onClose={() => navigate('/app/guest-transactions/overview')}
          onSuccess={handleTransactionCreated}
          transactionId={parseInt(transactionIdParam, 10)}
        />
      )}
    </TabbedPageLayout>
  );
};
